import { animate, state, style, transition, trigger } from "@angular/animations";
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, inject, input, signal, viewChild } from "@angular/core";
import { BiTranslatePipe } from "@globals/bi-translate";
import { googleApiKey } from "@globals/classes/global-constants";
import { BiCountryId } from "@globals/enums/BiLanguageAndCountryId";
import { isStringNullOrEmpty } from "@globals/helper-functions";
import { BiDomService } from "@globals/services/bi-dom.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { LatLng } from "leaflet";
import { InputTextModule } from "primeng/inputtext";

@UntilDestroy()
@Component({
  selector: "bi-leaflet-googlesearch",
  templateUrl: "./bi-leaflet-googlesearch.component.html",
  styleUrls: ["./bi-leaflet-googlesearch.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger("foldInOutHorizontal", [state("true", style({ width: "*" })), state("false", style({ width: "0", overflow: "hidden" })), transition("true <=> false", animate(200))])],
  imports: [InputTextModule, BiTranslatePipe],
  host: {
    class: "leaflet-control bi-leaflet-googlesearch"
  }
})
export class BiLeafletGoogleSearchComponent implements AfterViewInit {
  readonly map = input<L.Map>();

  readonly countryId = input<BiCountryId>();

  readonly inputExpanded = signal(false);

  private geocoder: any;

  private selectedAddress: google.maps.places.PlaceResult;

  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>("searchInput");

  private readonly domService = inject(BiDomService);

  public ngAfterViewInit() {
    this.setupGooglePlacesAutocomplete();
  }

  private setupGooglePlacesAutocomplete() {
    this.domService
      .loadScript("https://maps.googleapis.com/maps/api/js?key=" + googleApiKey + "&libraries=places&language=" + this.googleCountryCode(this.countryId()), "googleplaces")
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const autocomplete = new google.maps.places.Autocomplete(this.searchInput().nativeElement, {
          componentRestrictions: {
            country: this.iSO_3166_country_code(this.countryId())
          },
          fields: ["address_components", "geometry"],
          types: ["address"]
        });

        autocomplete.addListener("place_changed", () => this.onAddressChange(autocomplete.getPlace()));
      });
  }

  private onAddressChange(address: google.maps.places.PlaceResult) {
    if (address.geometry?.location) {
      this.selectedAddress = address;
      this.map().setView(new LatLng(address.geometry.location.lat(), address.geometry.location.lng()), 18);
    }
  }

  public onInputChanged() {
    if (this.selectedAddress) this.selectedAddress = undefined;
  }

  public onInputBlur(event: any) {
    if (!isStringNullOrEmpty(event.target.value)) {
      setTimeout(() => {
        if (!this.selectedAddress) this.geocodeAddress(event.target.value);
      }, 200);
    }
  }

  public inputMouseover() {
    this.map().dragging.disable();
    this.map().doubleClickZoom.disable();
  }

  public inputMouseout() {
    this.map().dragging.enable();
    this.map().doubleClickZoom.enable();
  }

  private geocodeAddress(location: string) {
    this.geocoder = new google.maps.Geocoder();
    this.geocoder.geocode(
      {
        address: location,
        componentRestrictions: {
          country: this.iSO_3166_country_code(this.countryId())
        }
      },
      (results, status) => {
        if (status == google.maps.GeocoderStatus.OK) {
          this.map().setView(new LatLng(results[0].geometry.location.lat(), results[0].geometry.location.lng()), 18);
        }
      }
    );
  }

  //https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes
  public iSO_3166_country_code(countryId: number): string {
    switch (countryId) {
      case BiCountryId.DK:
        return "DK";
      case BiCountryId.SE:
        return "SE";
      case BiCountryId.FI:
        return "FI";
      case BiCountryId.NO:
        return "NO";
      case BiCountryId.EN:
        return "GB";
    }
  }

  //https://developers.google.com/custom-search/docs/xml_results_appendices#countryCodes
  public googleCountryCode(countryId: number): string {
    switch (countryId) {
      case BiCountryId.DK:
        return "da";
      case BiCountryId.SE:
        return "sv";
      case BiCountryId.FI:
        return "fi";
      case BiCountryId.NO:
        return "no";
      case BiCountryId.EN:
        return "en";
    }
  }

  public toggleExpansion() {
    this.inputExpanded.update(value => !value);
    setTimeout(() => this.searchInput().nativeElement.focus(), 200); // To handle that Safari can't find out to set focus itself
  }

  public focusSearchInput() {
    setTimeout(() => this.searchInput().nativeElement.focus(), 200); // To handle that Safari can't find out to set focus itself
  }
}
